import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronLeft, CheckCircle, XCircle, Activity, Wind, Heart, Brain, Play, Square, RotateCcw } from 'lucide-react';
import SiteHeaderV2 from '@/components/website/SiteHeaderV2';
import SiteFooterV2 from '@/components/website/SiteFooterV2';
import PageTitle from '@/components/website/PageTitle';

/* eslint-disable react/no-unescaped-entities */

// V2 Theme Colors
const C = {
  white: '#FFFFFF',
  cloud: '#F8FAFC',
  sky: '#4A6D8C',
  skyLight: '#DCE6F0',
  skyDark: '#334F6B',
  ink: '#0F172A',
  slate: '#475569',
  muted: '#94A3B8',
};

const OptimalBreathingPage = () => {
  const [breathsPerMinute, setBreathsPerMinute] = useState(6);
  
  // Chronometer state
  const [chronometerTime, setChronometerTime] = useState(0);
  const [isChronometerRunning, setIsChronometerRunning] = useState(false);
  const [chronometerFinished, setChronometerFinished] = useState(false);
  const chronometerIntervalRef = useRef(null);

  const optimalCriteria = [
    {
      icon: Wind,
      title: "Respiration Nasale",
      description: "Le nez est fait pour respirer, la bouche pour manger et parler.",
      details: [
        "Filtration et purification de l'air",
        "Humidification à 80-90%",
        "Réchauffement à température corporelle",
        "Production d'oxyde nitrique (NO) vasodilatateur",
        "Résistance optimale pour le diaphragme",
        "Protection contre les infections respiratoires"
      ],
      color: "from-sky-500 to-blue-500"
    },
    {
      icon: Activity,
      title: "Respiration Diaphragmatique",
      description: "Le diaphragme doit assurer 70-80% du travail respiratoire.",
      details: [
        "Ventilation efficace des bases pulmonaires",
        "Activation du système parasympathique (relaxation)",
        "Massage des organes abdominaux",
        "Amélioration du retour veineux",
        "Stabilisation posturale du tronc",
        "Économie d'énergie (moins d'effort musculaire)"
      ],
      color: "from-teal-500 to-emerald-500"
    },
    {
      icon: Heart,
      title: "Basse Fréquence Respiratoire",
      description: "Entre 6 et 12 respirations par minute au repos.",
      details: [
        "Optimisation des échanges gazeux (O₂/CO₂)",
        "Maintien d'un CO₂ sanguin optimal",
        "Amélioration de la variabilité cardiaque",
        "Réduction du stress et de l'anxiété",
        "Meilleure oxygénation tissulaire",
        "Longévité accrue (études épidémiologiques)"
      ],
      color: "from-sky-500 to-sky-600"
    }
  ];

  const breathingComparison = [
    {
      type: "Optimale",
      characteristics: [
        "Nasale",
        "Diaphragmatique",
        "6-12 respirations/min",
        "Silencieuse",
        "Rythme régulier",
        "Ventre qui se gonfle"
      ],
      effects: [
        "Énergie stable",
        "Calme mental",
        "Bonne oxygénation",
        "Digestion optimale",
        "Sommeil réparateur"
      ],
      icon: CheckCircle,
      color: "bg-sky-50 border-sky-200 text-sky-700"
    },
    {
      type: "Dysfonctionnelle",
      characteristics: [
        "Buccale ou mixte",
        "Thoracique haute",
        "15-20+ respirations/min",
        "Visible/audible",
        "Irrégulière",
        "Épaules qui montent"
      ],
      effects: [
        "Fatigue chronique",
        "Anxiété accrue",
        "Hyperventilation",
        "Troubles digestifs",
        "Insomnies"
      ],
      icon: XCircle,
      color: "bg-rose-50 border-sky-200 text-sky-700"
    }
  ];

  const benefits = [
    {
      icon: Brain,
      title: "Performance Cognitive",
      description: "Une respiration lente et profonde améliore la concentration, la mémoire et la prise de décision."
    },
    {
      icon: Heart,
      title: "Santé Cardiovasculaire",
      description: "La cohérence cardiaque (6 respirations/min) optimise la variabilité cardiaque et réduit la tension artérielle."
    },
    {
      icon: Activity,
      title: "Résilience au Stress",
      description: "Une fréquence respiratoire basse active le système parasympathique et réduit le cortisol."
    },
    {
      icon: Wind,
      title: "Système Immunitaire",
      description: "La respiration nasale et l'oxyde nitrique renforcent les défenses naturelles contre les infections."
    }
  ];

  // Chronometer effect
  useEffect(() => {
    if (isChronometerRunning && chronometerTime < 60) {
      chronometerIntervalRef.current = setInterval(() => {
        setChronometerTime(prev => {
          if (prev >= 59) {
            setIsChronometerRunning(false);
            setChronometerFinished(true);
            clearInterval(chronometerIntervalRef.current);
            return 60;
          }
          return prev + 1;
        });
      }, 1000);
    }
    return () => {
      if (chronometerIntervalRef.current) {
        clearInterval(chronometerIntervalRef.current);
      }
    };
  }, [isChronometerRunning, chronometerTime]);

  const startChronometer = () => {
    setChronometerTime(0);
    setChronometerFinished(false);
    setIsChronometerRunning(true);
  };

  const stopChronometer = () => {
    setIsChronometerRunning(false);
    if (chronometerIntervalRef.current) {
      clearInterval(chronometerIntervalRef.current);
    }
  };

  const resetChronometer = () => {
    setIsChronometerRunning(false);
    setChronometerTime(0);
    setChronometerFinished(false);
    if (chronometerIntervalRef.current) {
      clearInterval(chronometerIntervalRef.current);
    }
  };

  const calculateCycleTime = (bpm) => {
    return 60 / bpm;
  };

  const getFrequencyStatus = (bpm) => {
    if (bpm >= 4 && bpm <= 5) return { label: "Niveau Yogi, respiration très lente", color: "text-slate-600 bg-sky-50" };
    if (bpm >= 6 && bpm <= 8) return { label: "Optimal (Cohérence Cardiaque)", color: "text-sky-600 bg-sky-50" };
    if (bpm >= 9 && bpm <= 12) return { label: "Très Bon", color: "text-sky-600 bg-sky-50" };
    if (bpm >= 13 && bpm <= 14) return { label: "Normal", color: "text-sky-600 bg-sky-50" };
    if (bpm >= 15 && bpm <= 20) return { label: "Élevé", color: "text-slate-600 bg-sky-50" };
    return { label: "Hyperventilation", color: "text-sky-600 bg-rose-50" };
  };

  const cycleTime = calculateCycleTime(breathsPerMinute);
  const status = getFrequencyStatus(breathsPerMinute);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FFFFFF", fontFamily: "Figtree, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500&family=Figtree:wght@400;500;600&display=swap');
      `}</style>
      
      <SiteHeaderV2 />
      
      {/* Hero Section */}
      <section className="py-16 lg:py-24" style={{ backgroundColor: "#F8FAFC" }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="max-w-6xl mx-auto">
            <PageTitle
              kicker="Comprendre"
              title="La Respiration"
              highlight="Optimale"
              subtitle="Découvrez les trois piliers d'une respiration saine et efficace pour une santé optimale."
              align="center"
            />
          </div>
        </div>
      </section>

      {/* Three Pillars */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6 sm:px-12">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl sm:text-4xl mb-4"
              style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: '#0F172A' }}
            >
              Les 3 Piliers de la <span style={{ color: '#0EA5E9', fontWeight: 400 }}>Respiration Optimale</span>
            </h2>
            <p className="max-w-2xl mx-auto" style={{ color: '#475569' }}>
              Une respiration optimale pour l'être humain repose sur trois caractéristiques fondamentales, 
              validées par des décennies de recherches en physiologie respiratoire.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {optimalCriteria.map((criterion, index) => (
              <div 
                key={index} 
                className="p-6 rounded-3xl transition-all hover:shadow-lg"
                style={{ backgroundColor: '#FFFFFF', border: '1px solid #F8FAFC' }}
              >
                <div 
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: '#E0F2FE' }}
                >
                  <criterion.icon className="w-7 h-7" style={{ color: '#0EA5E9' }} />
                </div>
                <h3 className="text-xl font-medium mb-2" style={{ color: '#0F172A' }}>{criterion.title}</h3>
                <p className="mb-4 italic" style={{ color: '#475569' }}>{criterion.description}</p>
                <ul className="space-y-2">
                  {criterion.details.map((detail, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm" style={{ color: '#475569' }}>
                      <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#0EA5E9' }} />
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Visual Summary */}
          <div 
            className="rounded-3xl p-8 text-center"
            style={{ backgroundColor: '#E0F2FE' }}
          >
            <h3 className="text-2xl font-medium mb-6" style={{ fontFamily: 'Outfit, sans-serif', color: '#0F172A' }}>
              En Résumé : La Formule de la Respiration Optimale
            </h3>
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-base font-medium">
              <div className="px-6 py-3 rounded-full" style={{ backgroundColor: '#FFFFFF', color: '#0F172A' }}>
                Respiration Nasale
              </div>
              <span className="text-xl hidden md:block" style={{ color: '#0EA5E9' }}>+</span>
              <div className="px-6 py-3 rounded-full" style={{ backgroundColor: '#FFFFFF', color: '#0F172A' }}>
                Diaphragmatique
              </div>
              <span className="text-xl hidden md:block" style={{ color: '#0EA5E9' }}>+</span>
              <div className="px-6 py-3 rounded-full" style={{ backgroundColor: '#FFFFFF', color: '#0F172A' }}>
                🐢 6-12 respirations/min
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Principles Section */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-medium mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              3 Principes Fondamentaux Supplémentaires
            </h2>
            <h3 className="text-2xl font-semibold text-sky-600 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              La Respiration LSD : Lente, Souple, Diaphragmatique
            </h3>
            <div className="text-slate-600 max-w-3xl mx-auto space-y-4 text-left">
              <p>
                La respiration de l'être humain est prévue pour être <strong>économe en ressources énergétiques</strong>, et pour ce faire, elle doit satisfaire à ces principes.
              </p>
              <p className="p-4 bg-sky-50 border-l-4 border-sky-500 rounded-r-lg">
                <strong className="text-slate-800"> Important :</strong> <span className="text-slate-700">N'oubliez pas que, même si cela est contre-intuitif, de grandes respirations, bien que physiquement satisfaisantes, entraînent une <strong>désoxygénation du corps</strong>. La respiration du quotidien se fait à minima, et lorsque le corps en ressent le besoin, il soupire, il bâille, pour maintenir la souplesse et l'élasticité des tissus.</span>
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Pilier 1 */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-xl">1</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-medium mb-3">Effectuer des Pauses entre Chaque Respiration</h3>
                    <p className="text-slate-600 leading-relaxed">
                      Intégrer de courtes pauses respiratoires (1-2 secondes) après l'inspiration et après l'expiration permet d'optimiser les échanges gazeux au niveau alvéolaire. Ces pauses naturelles favorisent une meilleure assimilation de l'oxygène et une régulation optimale du CO₂, tout en créant un rythme respiratoire plus harmonieux et apaisant.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pilier 2 */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-xl">2</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-medium mb-3">Une Respiration Douce et Sans Effort</h3>
                    <p className="text-slate-600 leading-relaxed">
                      La respiration doit être <strong>douce, sans effort</strong>, avec une inspiration <strong>progressive et continue</strong>. L'expiration, quant à elle, doit être un <strong>simple relâchement musculaire</strong> naturel, sans forcer ni contracter. Cette approche réduit le travail respiratoire, préserve l'énergie, et favorise un état de détente profonde. Une respiration forcée ou haletante crée des tensions inutiles et perturbe l'équilibre du système nerveux autonome.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pilier 3 */}
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-sky-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-xl">3</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-medium mb-3">Le Thorax Doit se Relâcher Complètement lors de l'Expiration</h3>
                    <p className="text-slate-600 leading-relaxed mb-4">
                      Un thorax qui se relâche complètement pendant l'expiration permet une <strong>expiration complète sans effort</strong> et induit une activation du <strong>système nerveux parasympathique</strong> (système de relaxation), par la voie du <strong>Nerf Vague</strong>.
                    </p>
                    <div className="p-4 bg-sky-50 rounded-lg border-l-4 border-sky-500">
                      <h4 className="font-semibold text-slate-800 mb-2"> Mécanisme Neurophysiologique</h4>
                      <p className="text-slate-700 text-sm leading-relaxed">
                        Le relâchement thoracique complet active les mécanorécepteurs et stimule le nerf vague, nerf principal du système parasympathique. Cette activation déclenche une cascade de réponses physiologiques : ralentissement cardiaque, réduction de la tension artérielle, amélioration de la digestion, et diminution du cortisol (hormone du stress). C'est le mécanisme clé derrière les effets calmants et régénérateurs d'une respiration correcte.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Summary box */}
          <div className="mt-8 p-6 bg-gradient-to-r from-sky-500 to-sky-600 rounded-2xl text-white">
            <h3 className="text-xl font-bold mb-3 text-center">💎 L'Art de la Respiration Parfaite</h3>
            <p className="text-center text-sky-100">
              Pauses naturelles + Douceur et progressivité + Relâchement thoracique complet = Activation parasympathique maximale
            </p>
          </div>

          {/* Soupirs et Bâillements */}
          <div className="mt-12">
            <h3 className="text-2xl font-medium mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Soupirs et Bâillements : Les Régulateurs Naturels
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Soupirs */}
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-400 to-blue-500 flex items-center justify-center">
                      <Wind className="w-5 h-5 text-white" />
                    </div>
                    <h4 className="text-xl font-medium">Soupirs (Involontaires)</h4>
                  </div>
                  
                  <div className="space-y-4 text-sm">
                    <div>
                      <h5 className="font-semibold text-slate-700 mb-1"> Rôle principal</h5>
                      <p className="text-slate-600">
                        Le soupir est un mécanisme de régulation respiratoire. Il permet une inspiration profonde qui rouvre les petites alvéoles pulmonaires qui se sont affaissées, assurant un échange gazeux optimal et évitant un collapsus pulmonaire.
                      </p>
                    </div>
                    
                    <div className="p-3 bg-sky-50 rounded-lg border-l-3 border-sky-400">
                      <h5 className="font-semibold text-slate-800 mb-1"> Fréquence excessive</h5>
                      <p className="text-slate-700 text-sm">
                        S'il est trop fréquent, un soupir excessif peut signaler un manque d'oxygène ou un déséquilibre dans le rythme respiratoire normal, souvent lié au stress, à l'anxiété ou à une ventilation inefficace.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Bâillements */}
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-white">
                        <circle cx="12" cy="12" r="10"/>
                        <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
                        <line x1="9" y1="9" x2="9.01" y2="9"/>
                        <line x1="15" y1="9" x2="15.01" y2="9"/>
                      </svg>
                    </div>
                    <h4 className="text-xl font-medium">Bâillements</h4>
                  </div>
                  
                  <div className="space-y-4 text-sm">
                    <div>
                      <h5 className="font-semibold text-slate-700 mb-2"> Rôles multiples</h5>
                      <ul className="text-slate-600 space-y-1">
                        <li><strong>Stimulation et éveil :</strong> Augmente la vigilance, désengage le réseau neuronal par défaut (DMN) pour se concentrer sur l'environnement.</li>
                        <li><strong>Thermorégulation cérébrale :</strong> Rafraîchit le cerveau, car une grande inspiration d'air frais aide à abaisser sa température.</li>
                        <li><strong>Oxygénation/CO₂ :</strong> Assure une grande bouffée d'air, potentiellement pour compenser une légère hypoxie ou un déséquilibre.</li>
                        <li><strong>Communication :</strong> Peut avoir une fonction sociale, indiquant un état émotionnel ou un niveau d'énergie.</li>
                      </ul>
                    </div>
                    
                    <div className="p-3 bg-violet-50 rounded-lg border-l-3 border-violet-400">
                      <h5 className="font-semibold text-violet-800 mb-1"> Fréquence - Signes d'alerte</h5>
                      <ul className="text-violet-700 text-sm space-y-1">
                        <li>• <strong>Fatigue/ennui :</strong> Signe classique de manque de sommeil ou de monotonie.</li>
                        <li>• <strong>Besoin accru d'oxygène :</strong> Peut indiquer une hypoxie, des déséquilibres neurologiques, ou être un effet secondaire médicamenteux.</li>
                        <li>• <strong>Excès :</strong> Peut être le symptôme du syndrome d'hyperventilation chronique, où une respiration trop ample perturbe les niveaux de CO₂ et d'O₂.</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Breathing Frequency Calculator */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-medium mb-8 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Calculateur de Fréquence Respiratoire
          </h2>
          
          {/* Chronometer Section */}
          <Card className="border-0 shadow-xl mb-8">
            <CardContent className="p-8">
              <h3 className="text-xl font-medium mb-4 text-center">
                🕐 Mesurez Votre Fréquence Respiratoire Actuelle
              </h3>
              
              <div className="p-4 bg-sky-50 border-l-4 border-sky-500 rounded-lg mb-6">
                <p className="text-slate-700 leading-relaxed">
                  <strong>Instructions :</strong> Posez une main à cheval sur le ventre et les dernières côtes et, 
                  au lancement du chronomètre, comptez le nombre de respirations jusqu'à l'arrêt du chronomètre, 
                  afin de mesurer votre fréquence respiratoire actuelle.
                </p>
              </div>

              <div className="flex flex-col items-center">
                {/* Chronometer Display */}
                <div className="relative mb-6">
                  <div 
                    className={`w-40 h-40 rounded-full flex items-center justify-center border-4 ${
                      isChronometerRunning 
                        ? 'border-sky-500 bg-sky-50' 
                        : chronometerFinished 
                        ? 'border-sky-500 bg-sky-50' 
                        : 'border-slate-300'
                    } transition-colors duration-300`}
                  >
                    <div className="text-center">
                      <div className={`text-5xl font-bold ${
                        isChronometerRunning 
                          ? 'text-sky-600' 
                          : chronometerFinished 
                          ? 'text-sky-600' 
                          : 'text-slate-600'
                      }`}>
                        {chronometerTime}
                      </div>
                      <div className="text-sm text-slate-500">secondes</div>
                    </div>
                  </div>
                  
                  {/* Progress Ring */}
                  <svg 
                    className="absolute inset-0 w-40 h-40 -rotate-90"
                    viewBox="0 0 160 160"
                  >
                    <circle
                      cx="80"
                      cy="80"
                      r="76"
                      fill="none"
                      stroke={isChronometerRunning ? "#14b8a6" : chronometerFinished ? "#22c55e" : "#e2e8f0"}
                      strokeWidth="4"
                      strokeDasharray={`${(chronometerTime / 60) * 477.5} 477.5`}
                      className="transition-all duration-1000"
                    />
                  </svg>
                </div>

                {/* Controls */}
                <div className="flex gap-4">
                  {!isChronometerRunning && !chronometerFinished && (
                    <button
                      onClick={startChronometer}
                      className="flex items-center gap-2 px-6 py-3 bg-cyan-500 hover:bg-cyan-600 text-white rounded-full font-semibold transition-colors shadow-lg"
                    >
                      <Play size={20} />
                      Démarrer
                    </button>
                  )}
                  
                  {isChronometerRunning && (
                    <button
                      onClick={stopChronometer}
                      className="flex items-center gap-2 px-6 py-3 bg-sky-500 hover:bg-orange-600 text-white rounded-full font-semibold transition-colors shadow-lg"
                    >
                      <Square size={20} />
                      Arrêter
                    </button>
                  )}
                  
                  {(chronometerFinished || (!isChronometerRunning && chronometerTime > 0)) && (
                    <button
                      onClick={resetChronometer}
                      className="flex items-center gap-2 px-6 py-30 hover:bg-slate-600 text-white rounded-full font-semibold transition-colors shadow-lg"
                    >
                      <RotateCcw size={20} />
                      Recommencer
                    </button>
                  )}
                </div>

                {chronometerFinished && (
                  <div className="mt-6 p-4 bg-sky-50 border-l-4 border-sky-500 rounded-lg max-w-md">
                    <p className="text-sky-800 font-semibold mb-2"> Chronomètre terminé !</p>
                    <p className="text-sky-700 text-sm">
                      Notez le nombre de respirations que vous avez comptées. 
                      Utilisez ensuite le calculateur ci-dessous pour évaluer votre fréquence respiratoire.
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Calculator Card */}
          <Card className="border-0 shadow-xl">
            <CardContent className="p-8">
              <div className="text-center mb-6">
                <p className="text-slate-600 mb-4">Ajustez le curseur pour voir l'impact de la fréquence respiratoire</p>
                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={() => setBreathsPerMinute(Math.max(4, breathsPerMinute - 1))}
                    className="w-10 h-10 rounded-full bg-sky-100 hover:bg-teal-200 text-sky-600 font-bold"
                  >
                    -
                  </button>
                  <div className="text-center">
                    <div className="text-5xl font-bold text-sky-600">{breathsPerMinute}</div>
                    <div className="text-sm text-slate-500">respirations/minute</div>
                  </div>
                  <button
                    onClick={() => setBreathsPerMinute(Math.min(25, breathsPerMinute + 1))}
                    className="w-10 h-10 rounded-full bg-sky-100 hover:bg-teal-200 text-sky-600 font-bold"
                  >
                    +
                  </button>
                </div>
                
                <div className="mt-4">
                  <input
                    type="range"
                    min="4"
                    max="25"
                    value={breathsPerMinute}
                    onChange={(e) => setBreathsPerMinute(parseInt(e.target.value))}
                    className="w-full max-w-md"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mt-8">
                <div className="p-4 rounded-xl">
                  <h4 className="font-semibold text-slate-700 mb-2">Durée d'un Cycle Complet</h4>
                  <p className="text-3xl font-medium">{cycleTime.toFixed(1)}s</p>
                  <p className="text-sm text-slate-500 mt-1">
                    (Inspiration + Expiration + Pauses)
                  </p>
                </div>
                
                <div className={`p-4 rounded-xl ${status.color}`}>
                  <h4 className="font-semibold mb-2">Évaluation</h4>
                  <p className="text-2xl font-bold">{status.label}</p>
                  <p className="text-sm mt-1 opacity-80">
                    {breathsPerMinute >= 4 && breathsPerMinute <= 5
                      ? "Maîtrise avancée de la respiration"
                      : breathsPerMinute >= 6 && breathsPerMinute <= 12 
                      ? "Fréquence optimale pour la santé"
                      : breathsPerMinute >= 13 && breathsPerMinute <= 14
                      ? "Dans la norme mais perfectible"
                      : "Nécessite une rééducation respiratoire"}
                  </p>
                </div>
              </div>

              {(breathsPerMinute === 4 || breathsPerMinute === 5) && (
                <div className="mt-6 p-4 bg-sky-50 border-l-4 border-sky-500 rounded-lg">
                  <p className="text-slate-800 font-semibold">
                    🧘 Niveau Yogi !
                  </p>
                  <p className="text-slate-700 text-sm mt-1">
                    {breathsPerMinute} respirations par minute est une fréquence très lente, typique des pratiquants 
                    avancés de yoga et de méditation. Cette maîtrise respiratoire exceptionnelle témoigne d'un 
                    excellent contrôle du système nerveux autonome.
                  </p>
                </div>
              )}

              {breathsPerMinute === 6 && (
                <div className="mt-6 p-4 bg-sky-50 border-l-4 border-sky-500 rounded-lg">
                  <p className="text-sky-800 font-semibold">
                     Cohérence Cardiaque Parfaite !
                  </p>
                  <p className="text-sky-700 text-sm mt-1">
                    6 respirations par minute = 5s inspiration + 5s expiration. 
                    C'est la fréquence idéale pour maximiser la variabilité cardiaque et réduire le stress.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-medium mb-8 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Respiration Optimale vs Dysfonctionnelle
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {breathingComparison.map((type, index) => (
              <Card key={index} className={`border-2 ${type.color}`}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <type.icon className="w-8 h-8" />
                    <h3 className="text-2xl font-bold">{type.type}</h3>
                  </div>
                  
                  <div className="mb-4">
                    <h4 className="font-semibold mb-2">Caractéristiques :</h4>
                    <ul className="space-y-1">
                      {type.characteristics.map((char, i) => (
                        <li key={i} className="text-sm flex items-center gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-current" />
                          {char}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold mb-2">Effets sur la santé :</h4>
                    <ul className="space-y-1">
                      {type.effects.map((effect, i) => (
                        <li key={i} className="text-sm flex items-center gap-2">
                          {index === 0 ? '' : ''} {effect}
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-medium mb-8 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Les Bénéfices d'une Respiration Optimale
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-0 shadow-md hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center flex-shrink-0">
                      <benefit.icon className="w-6 h-6 text-sky-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-medium mb-2">{benefit.title}</h3>
                      <p className="text-slate-600">{benefit.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Scientific Evidence */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-medium mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Pourquoi 6 à 12 Respirations par Minute ?
          </h2>
          
          <div className="prose prose-lg max-w-none text-slate-600 space-y-4">
            <p>
              La fréquence respiratoire optimale de <strong>6 à 12 respirations par minute</strong> n'est pas arbitraire. 
              Elle découle de décennies de recherches en physiologie respiratoire et cardiovasculaire.
            </p>
            
            <Card className="border-0 bg-sky-50 border-l-4 border-sky-500">
              <CardContent className="p-6">
                <h3 className="font-bold text-slate-800 mb-3">🔬 Explications Physiologiques</h3>
                <ul className="space-y-2 text-slate-700">
                  <li>
                    <strong>Équilibre CO₂/O₂ :</strong> Une respiration trop rapide évacue trop de CO₂, 
                    réduisant paradoxalement l'oxygénation tissulaire (effet Bohr). Une fréquence de 6-12/min 
                    maintient le CO₂ à un niveau optimal de 40 mmHg.
                  </li>
                  <li>
                    <strong>Variabilité Cardiaque :</strong> À 6 respirations/min (0,1 Hz), on atteint la 
                    fréquence de résonance du baroréflexe, maximisant la variabilité cardiaque (HRV), marqueur 
                    de santé cardiovasculaire et de longévité.
                  </li>
                  <li>
                    <strong>Efficience Énergétique :</strong> Une respiration lente et profonde mobilise 
                    moins d'énergie que de multiples respirations superficielles. Le coût énergétique de 
                    la respiration passe de 3% (repos) à moins de 2%.
                  </li>
                  <li>
                    <strong>Activation Parasympathique :</strong> Les respirations lentes stimulent les 
                    barorécepteurs et le nerf vague, activant le système parasympathique (repos et digestion) 
                    et réduisant le cortisol.
                  </li>
                </ul>
              </CardContent>
            </Card>

            <div className="mt-6 p-6 border-l-4 border-sky-400 rounded-lg">
              <h4 className="font-medium mb-2"> La Norme n'est Pas Toujours l'Optimal</h4>
              <p className="text-slate-800">
                La "norme" médicale est de 12-20 respirations/minute chez l'adulte. Cependant, cette norme 
                reflète la moyenne de la population moderne, souvent stressée et sédentaire. Les populations 
                traditionnelles, les athlètes d'endurance et les pratiquants de méditation affichent 
                naturellement des fréquences plus basses (6-10/min), associées à une meilleure santé globale.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Action Steps */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl font-medium mb-8 text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Comment Optimiser Votre Respiration ?
          </h2>
          
          <div className="space-y-6">
            <Card className="border-0 shadow-md">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-sky-600 font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium mb-2">Testez Votre Respiration Actuelle</h3>
                    <p className="text-slate-600">
                      Asseyez-vous confortablement et comptez vos respirations pendant 1 minute. 
                      Si vous êtes au-dessus de 12/min, une rééducation est recommandée.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-sky-600 font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium mb-2">Pratiquez la Respiration Guidée</h3>
                    <p className="text-slate-600">
                      Utilisez nos exercices guidés (Cohérence Cardiaque, Buteyko, Respiration Carrée) 
                      pour apprendre progressivement à ralentir votre respiration.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-sky-600 font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium mb-2">Fermez la Bouche</h3>
                    <p className="text-slate-600">
                      Respirez exclusivement par le nez, même pendant l'effort (adapté progressivement). 
                      Utilisez du tape médical la nuit si nécessaire.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-md">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-sky-600 font-bold">4</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium mb-2">Entraînez le Diaphragme</h3>
                    <p className="text-slate-600">
                      Allongez-vous sur le dos, une main sur le ventre. À l'inspiration, seul le ventre 
                      doit se soulever. Pratiquez 5 minutes par jour.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-medium mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Commencez Votre Rééducation Respiratoire
          </h2>
          <p className="text-slate-600 text-lg mb-8 max-w-2xl mx-auto">
            Apprenez à respirer de manière optimale avec nos exercices guidés basés sur les principes 
            de la respiration nasale, diaphragmatique et à basse fréquence.
          </p>
          <Link to="/app-landing">
            <button className="px-10 py-4 text-lg rounded-full bg-cyan-500 text-white hover:bg-cyan-600 shadow-lg font-semibold transition-colors">
              Accéder aux exercices
            </button>
          </Link>
        </div>
      </section>

      <SiteFooterV2 />
    </div>
  );
};

export default OptimalBreathingPage;
